/**
 * Find and replace in the open file: CodeMirror's search state with our own bar
 * (project/FindBar.tsx, rendered into the panel this creates) instead of its default one.
 * Adds search-in-selection, a match count, search-as-you-type, and remembers the
 * query and options across files.
 */
import {
  closeSearchPanel,
  getSearchQuery,
  openSearchPanel,
  search,
  SearchQuery,
  searchPanelOpen,
  selectMatches,
  setSearchQuery,
} from "@codemirror/search";
import { EditorSelection, StateEffect, StateField, type EditorState, type Extension } from "@codemirror/state";
import { Decoration, EditorView, type KeyBinding, type Panel, type ViewUpdate } from "@codemirror/view";

export interface Range {
  from: number;
  to: number;
}

export type FindOption = "caseSensitive" | "wholeWord" | "regexp" | "inSelection";

interface QueryFields {
  search: string;
  replace: string;
  caseSensitive: boolean;
  regexp: boolean;
  wholeWord: boolean;
}

export const isMac = /Mac|iPhone|iPad/.test(navigator.platform);
/** Shortcut labels for the option toggles, matching findKeymap. */
export const optionKeys: Record<FindOption, string> = {
  caseSensitive: isMac ? "⌥⌘C" : "Alt+C",
  wholeWord: isMac ? "⌥⌘W" : "Alt+W",
  regexp: isMac ? "⌥⌘R" : "Alt+R",
  inSelection: isMac ? "⌥⌘L" : "Alt+L",
};

/** Limits matches to these ranges ("search in selection"); null searches the whole file. */
const setFindScope = StateEffect.define<readonly Range[] | null>();

const scopeMark = Decoration.mark({ class: "cm-findScope" });

const findScope = StateField.define<readonly Range[] | null>({
  create: () => null,
  update(scope, tr) {
    if (scope && tr.docChanged) scope = scope.map((r) => ({ from: tr.changes.mapPos(r.from, -1), to: tr.changes.mapPos(r.to, 1) }));
    for (const e of tr.effects) {
      if (e.is(setFindScope)) scope = e.value;
      // Queries CodeMirror makes itself (e.g. F3 with nothing to find) don't carry the scope test.
      else if (e.is(setSearchQuery) && e.value.test !== inScope) scope = null;
    }
    return scope;
  },
  provide: (f) =>
    EditorView.decorations.from(f, (scope) =>
      scope ? Decoration.set(scope.filter((r) => r.to > r.from).map((r) => scopeMark.range(r.from, r.to)), true) : Decoration.none,
    ),
});

function inScope(_match: string, state: EditorState, from: number, to: number) {
  const scope = state.field(findScope, false);
  return !scope || scope.some((r) => from >= r.from && to <= r.to);
}

/**
 * Searches are always literal: by default CodeMirror reads \n, \t and \r in the query
 * as newline, tab and return, so `\textbf` or `\ref` would never be found.
 */
function makeQuery(f: QueryFields) {
  return new SearchQuery({ ...f, literal: true, test: inScope });
}

function fields(q: SearchQuery): QueryFields {
  return { search: q.search, replace: q.replace, caseSensitive: q.caseSensitive, regexp: q.regexp, wholeWord: q.wholeWord };
}

export function setQuery(view: EditorView, change: Partial<QueryFields>) {
  view.dispatch({ effects: setSearchQuery.of(makeQuery({ ...fields(getSearchQuery(view.state)), ...change })) });
}

// ---------------------------------------------------------------- memory across files and reloads

const OPTS_KEY = "benleaf:find:opts";
const REPLACE_KEY = "benleaf:find:replace";

function load<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw === null ? fallback : { ...fallback, ...JSON.parse(raw) };
  } catch {
    return fallback;
  }
}
function save(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode or full storage: just don't remember.
  }
}

let last: QueryFields = { search: "", replace: "", ...load(OPTS_KEY, { caseSensitive: false, regexp: false, wholeWord: false }) };
let lastReplace = load(REPLACE_KEY, { open: false }).open;
/** Whether the bar was open, so it stays open when you switch files. */
let wasOpen = false;

function remember(q: SearchQuery) {
  const next = fields(q);
  if (next.caseSensitive !== last.caseSensitive || next.regexp !== last.regexp || next.wholeWord !== last.wholeWord) {
    save(OPTS_KEY, { caseSensitive: next.caseSensitive, regexp: next.regexp, wholeWord: next.wholeWord });
  }
  last = next;
}

// ---------------------------------------------------------------- the panel

const panels = new WeakMap<EditorView, FindPanel>();
let pendingFocus: "search" | "replace" | null = "search";

/** The empty panel CodeMirror shows; FindBar renders into `dom` through a portal. */
export class FindPanel implements Panel {
  readonly dom = document.createElement("div");
  /** Docked along the bottom of the editor; the toolbar has the top. */
  readonly top = false;
  /** Search-as-you-type starts here: where the cursor was when the bar opened. */
  anchor: number;
  /** The selection when the bar opened, for searching in it after you've moved to a match. */
  opened: readonly Range[];
  showReplace = lastReplace;
  /** Read and cleared by FindBar after it renders. */
  focus: "search" | "replace" | null = pendingFocus;
  private version = 0;
  private listeners = new Set<() => void>();

  constructor(
    readonly view: EditorView,
    private onPanel: (panel: FindPanel | null) => void,
  ) {
    this.dom.className = "cm-find";
    const sel = view.state.selection;
    this.anchor = sel.main.from;
    this.opened = sel.ranges.filter((r) => !r.empty).map((r) => ({ from: r.from, to: r.to }));
    panels.set(view, this);
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  };
  getVersion = () => this.version;
  changed() {
    this.version++;
    for (const fn of this.listeners) fn();
  }

  update(u: ViewUpdate) {
    if (u.docChanged) {
      this.anchor = u.changes.mapPos(this.anchor);
      this.opened = this.opened.map((r) => ({ from: u.changes.mapPos(r.from, -1), to: u.changes.mapPos(r.to, 1) }));
    }
    if (u.docChanged || u.selectionSet || u.startState.readOnly !== u.state.readOnly || u.transactions.some((tr) => tr.effects.length)) {
      for (const tr of u.transactions) for (const e of tr.effects) if (e.is(setSearchQuery)) remember(e.value);
      this.changed();
    }
  }

  mount() {
    wasOpen = true;
    this.onPanel(this);
  }

  destroy() {
    if (panels.get(this.view) === this) panels.delete(this.view);
    this.onPanel(null);
  }

  setShowReplace(open: boolean) {
    this.showReplace = lastReplace = open;
    save(REPLACE_KEY, { open });
    this.changed();
  }

  /** Select the first match from where the search started, wrapping round. */
  findFromAnchor() {
    const { state } = this.view;
    const q = getSearchQuery(state);
    if (!q.valid) return;
    const at = Math.min(this.anchor, state.doc.length);
    let m = q.getCursor(state, at).next();
    if (m.done) m = q.getCursor(state, 0, at).next();
    if (m.done) return;
    const range = EditorSelection.range(m.value.from, m.value.to);
    this.view.dispatch({ selection: EditorSelection.create([range]), effects: scrollToMatch(range), userEvent: "select.search" });
  }
}

const scrollToMatch = (range: Range) => EditorView.scrollIntoView(EditorSelection.range(range.from, range.to), { yMargin: 80 });

// ---------------------------------------------------------------- commands

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Open the find bar, or move to it, searching for the selected text if there is some.
 * With `replace`, the replace row opens and gets the focus. Use this rather than
 * CodeMirror's openSearchPanel. With `focus: false` the editor keeps the focus.
 */
export function openFind(view: EditorView, { replace = false, focus = true }: { replace?: boolean; focus?: boolean } = {}): boolean {
  const { state } = view;
  const existing = panels.get(view);
  if (existing && existing.dom.contains(document.activeElement)) {
    if (replace && !existing.showReplace) existing.setShowReplace(true);
    existing.focus = replace ? "replace" : "search";
    existing.changed();
    return true;
  }
  const q = fields(getSearchQuery(state));
  const sel = state.selection.main;
  const text = state.sliceDoc(sel.from, sel.to);
  const seed = focus && text && text.length <= 200 && !text.includes("\n") ? (q.regexp ? escapeRegExp(text) : text) : null;
  const target = focus ? (replace ? "replace" : "search") : null;
  pendingFocus = target;
  if (!searchPanelOpen(state)) openSearchPanel(view);
  pendingFocus = "search";
  setQuery(view, { ...q, search: seed ?? q.search });
  const panel = panels.get(view);
  if (panel) {
    panel.anchor = view.state.selection.main.from;
    panel.opened = view.state.selection.ranges.filter((r) => !r.empty).map((r) => ({ from: r.from, to: r.to }));
    if (replace && !panel.showReplace && !view.state.readOnly) panel.setShowReplace(true);
    panel.focus = target;
    panel.changed();
  }
  return true;
}

export function closeFind(view: EditorView): boolean {
  if (!searchPanelOpen(view.state)) return false;
  wasOpen = false;
  if (view.state.field(findScope, false)) view.dispatch({ effects: setFindScope.of(null) });
  return closeSearchPanel(view);
}

/** The ranges "search in selection" would use: the selection, unless it's just the match the bar moved to. */
export function scopeCandidate(view: EditorView): readonly Range[] {
  const { state } = view;
  const current = state.selection.ranges.filter((r) => !r.empty);
  if (current.length > 1 || (current.length === 1 && !isMatch(state, current[0]))) return current.map((r) => ({ from: r.from, to: r.to }));
  return panels.get(view)?.opened.filter((r) => r.to > r.from) ?? [];
}

function isMatch(state: EditorState, r: Range) {
  const q = getSearchQuery(state);
  if (!q.valid) return false;
  const m = q.getCursor(state, r.from, r.to).next();
  return !m.done && m.value.from === r.from && m.value.to === r.to;
}

export function findScopeOf(state: EditorState) {
  return state.field(findScope, false) ?? null;
}

export function toggleFind(view: EditorView, option: FindOption): boolean {
  if (!searchPanelOpen(view.state)) return false;
  const q = fields(getSearchQuery(view.state));
  if (option === "inSelection") {
    const scope = findScopeOf(view.state) ? null : scopeCandidate(view);
    if (scope && !scope.length) return true;
    // A fresh query object as well, so the match highlighting is redrawn for the new scope.
    view.dispatch({ effects: [setFindScope.of(scope), setSearchQuery.of(makeQuery(q))] });
  } else setQuery(view, { [option]: !q[option] });
  panels.get(view)?.findFromAnchor();
  return true;
}

/** Put a cursor on every match (in the selection, if searching there) and go back to the text. */
export function selectAllMatches(view: EditorView): boolean {
  if (!selectMatches(view)) return false;
  view.focus();
  return true;
}

export const MAX_COUNT = 9999;

/** Every match of the current query, up to MAX_COUNT; null when there's no valid query. */
export function allMatches(state: EditorState): { matches: Range[]; capped: boolean } | null {
  const q = getSearchQuery(state);
  if (!q.valid) return null;
  const matches: Range[] = [];
  const cursor = q.getCursor(state);
  for (let m = cursor.next(); !m.done; m = cursor.next()) {
    if (matches.length === MAX_COUNT) return { matches, capped: true };
    matches.push(m.value);
  }
  return { matches, capped: false };
}

/** Index of the match that is exactly the selection, or -1. */
export function matchIndex(matches: Range[], sel: Range): number {
  let lo = 0;
  let hi = matches.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const m = matches[mid];
    if (m.from < sel.from) lo = mid + 1;
    else if (m.from > sel.from) hi = mid - 1;
    else return m.to === sel.to ? mid : -1;
  }
  return -1;
}

/** Why the regular expression doesn't compile, with the flags CodeMirror uses. */
export function regexError(q: SearchQuery): string | null {
  if (!q.regexp || !q.search || q.valid) return null;
  try {
    new RegExp(q.search, "gmu");
    return null;
  } catch (e) {
    // Chrome says "Invalid regular expression: /(a/gmu: Unterminated group".
    return (e as Error).message.replace(/^Invalid regular expression: \/[\s\S]*\/[a-z]*: /i, "");
  }
}

/** Goes before CodeMirror's searchKeymap, whose Mod-f and Escape these replace. */
export const findKeymap: readonly KeyBinding[] = [
  { key: "Mod-f", run: (v) => openFind(v), scope: "editor search-panel", preventDefault: true },
  { key: "Ctrl-h", mac: "Mod-Alt-f", run: (v) => openFind(v, { replace: true }), scope: "editor search-panel", preventDefault: true },
  { key: "Escape", run: closeFind, scope: "editor search-panel" },
  { key: "Alt-c", mac: "Mod-Alt-c", run: (v) => toggleFind(v, "caseSensitive"), scope: "editor search-panel" },
  { key: "Alt-w", mac: "Mod-Alt-w", run: (v) => toggleFind(v, "wholeWord"), scope: "editor search-panel" },
  { key: "Alt-r", mac: "Mod-Alt-r", run: (v) => toggleFind(v, "regexp"), scope: "editor search-panel" },
  { key: "Alt-l", mac: "Mod-Alt-l", run: (v) => toggleFind(v, "inSelection"), scope: "editor search-panel" },
  { key: "Alt-Enter", run: selectAllMatches, scope: "search-panel" },
];

/**
 * The search state and panel. onPanel hears when the bar opens (with the panel to
 * render into) and closes. A new editor picks up the last query, and reopens the bar
 * if it was open in the previous file.
 */
export function find(onPanel: (panel: FindPanel | null) => void): Extension {
  return [
    search({ top: false, literal: true, createPanel: (view) => new FindPanel(view, onPanel), scrollToMatch }),
    findScope,
  ];
}

/** Call once a new editor exists. */
export function restoreFind(view: EditorView) {
  view.dispatch({ effects: setSearchQuery.of(makeQuery(last)) });
  if (wasOpen) openFind(view, { focus: false });
}
